import {ThemeControl} from './theme';
import React, { Suspense, useEffect, useRef, useState } from "react";
import "./index.css";
import api from "./axiosConfig";
import { flushSync } from "react-dom";
import PublicLanding from "./components/PublicLanding";
import Menu from "./components/Menu";
import NoteCard from "./components/Card";
import RecentTitlesSlider from "./components/RecentTitlesSlider";
import ConfigGeneratorView from "./components/ConfigGeneratorView";
import CalendarView from "./components/CalendarView";
import FlightsView from "./components/FlightsView";
import LearningView from "./components/LearningView";
import LearningLessonView from "./components/LearningLessonView";
import NoteGroupView from "./components/NoteGroupView";
import ProfileSettingsForm from "./components/ProfileSettingsForm";
import PublicShareView from "./components/PublicShareView";
import useSidebarChildrenCache from "./hooks/useSidebarChildrenCache";
import useSidebarDrawer from "./hooks/useSidebarDrawer";
import useTitleLibrary from "./hooks/useTitleLibrary";

const DeviceFormOne = React.lazy(() => import("./components/DeviceFormOne"));
const DeviceFormDuo = React.lazy(() => import("./components/DeviceFormDuo"));
const DeviceFormThree = React.lazy(() => import("./components/DeviceFormThree"));
const DeviceFormFour = React.lazy(() => import("./components/DeviceFormFour"));
const DeviceFormFive = React.lazy(() => import("./components/DeviceFormFive"));
const DeviceFormSix = React.lazy(() => import("./components/DeviceFormSix"));
const DeviceFormSeven = React.lazy(() => import("./components/DeviceFormSeven"));
const DeviceFormEight = React.lazy(() => import("./components/DeviceFormEight"));
const DeviceFormNine = React.lazy(() => import("./components/DeviceFormNine"));
const DeviceFormTen = React.lazy(() => import("./components/DeviceFormTen"));
const NoteView = React.lazy(() => import("./components/NoteView"));

const PAGE_SIZE = 9;
const SIDEBAR_PREF_KEY = "addanote.sidebarCollapsed.desktop.v2";
const APP_HISTORY_KEY = "__addanoteView";
const FOLDER_DRAG_MIME = "application/x-addanote-folder";
const NOTES_TRANSFER_STATUS_KEY = "addanote.notes.transferStatus.v1";
const NOTES_IMPORT_CHUNK_CHAR_LIMIT = 250_000;
const legacyViewComponents = {
  note1_link1: DeviceFormOne,
  note1_link2: DeviceFormDuo,
  note1_link3: DeviceFormThree,
  note1_link4: DeviceFormFour,
  note1_link5: DeviceFormFive,
  note2_link1: DeviceFormSix,
  note2_link2: DeviceFormSeven,
  note2_link3: DeviceFormEight,
  note2_link4: DeviceFormNine,
  note2_link5: DeviceFormTen,
};

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="menu-icon-svg">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatDate(value) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function buildHistorySnapshot({
  activeView,
  selectedGroup,
  editorState,
  groupInitialFolderPath,
  groupInitialEntryId,
  expandedSidebarGroupId,
  learningPageType,
  learningPageId,
  learningLessonItemId,
}) {
  return {
    [APP_HISTORY_KEY]: true,
    activeView,
    selectedGroup,
    editorState,
    groupInitialFolderPath,
    groupInitialEntryId,
    expandedSidebarGroupId,
    learningPageType,
    learningPageId,
    learningLessonItemId,
  };
}

function getDefaultHistorySnapshot() {
  return {
    [APP_HISTORY_KEY]: true,
    activeView: "dashboard",
    selectedGroup: null,
    editorState: null,
    groupInitialFolderPath: "",
    groupInitialEntryId: "",
    expandedSidebarGroupId: "",
    learningPageType: "home",
    learningPageId: "",
    learningLessonItemId: "",
  };
}

function decodeRouteSegment(value = "") {
  try {
    return decodeURIComponent(value);
  } catch {
    return "";
  }
}

function parseAppPath(pathname = "") {
  const normalized = String(pathname || "");
  if (/^\/login\/?$/.test(normalized)) return { ...getDefaultHistorySnapshot(), activeView: "login" };
  if (/^\/welcome\/?$/.test(normalized)) return { ...getDefaultHistorySnapshot(), activeView: "welcome" };
  if (/^\/flights\/?$/.test(normalized)) return { ...getDefaultHistorySnapshot(), activeView: "flights" };
  const noteMatch = normalized.match(/^\/notes\/([^/]+)(?:\/entries\/([^/]+))?\/?$/);
  if (noteMatch) {
    const groupId = decodeRouteSegment(noteMatch[1]);
    const entryId = decodeRouteSegment(noteMatch[2]);
    if (groupId) {
      return {
        activeView: "group",
        selectedGroup: { id: groupId },
        groupInitialFolderPath: "",
        groupInitialEntryId: entryId,
        learningPageType: "home",
        learningPageId: "",
        learningLessonItemId: "",
      };
    }
  }

  const lessonMatch = normalized.match(/^\/learning\/lesson\/([^/]+)$/);
  if (lessonMatch) {
    return {
      activeView: "learning-lesson",
      selectedGroup: null,
      groupInitialFolderPath: "",
      groupInitialEntryId: "",
      learningPageType: "home",
      learningPageId: "",
      learningLessonItemId: decodeRouteSegment(lessonMatch[1]),
    };
  }

  const homeMatch = normalized.match(/^\/learning\/?$/);
  if (homeMatch) {
    return {
      activeView: "learning",
      selectedGroup: null,
      groupInitialFolderPath: "",
      groupInitialEntryId: "",
      learningPageType: "home",
      learningPageId: "",
      learningLessonItemId: "",
    };
  }

  const routePatterns = [
    { type: "library", pattern: /^\/learning\/library\/([^/]+)$/ },
    { type: "course", pattern: /^\/learning\/course\/([^/]+)$/ },
    { type: "program", pattern: /^\/learning\/program\/([^/]+)$/ },
    { type: "module", pattern: /^\/learning\/module\/([^/]+)$/ },
  ];
  for (const route of routePatterns) {
    const match = normalized.match(route.pattern);
    if (match) {
      return {
        activeView: "learning",
        selectedGroup: null,
        groupInitialFolderPath: "",
        groupInitialEntryId: "",
        learningPageType: route.type,
        learningPageId: decodeRouteSegment(match[1]),
        learningLessonItemId: "",
      };
    }
  }

  return {
    activeView: "dashboard",
    selectedGroup: null,
    groupInitialFolderPath: "",
    groupInitialEntryId: "",
    learningPageType: "home",
    learningPageId: "",
    learningLessonItemId: "",
  };
}

function buildAppPath(snapshot = {}) {
  if (snapshot?.activeView === "login") return new URLSearchParams(window.location.search).get("mode") === "register" ? "/login?mode=register" : "/login";
  if (snapshot?.activeView === "welcome") return "/welcome";
  if (snapshot?.activeView === "flights") return "/flights";
  if ((snapshot?.activeView === "group" || snapshot?.activeView === "entry-editor") && snapshot?.selectedGroup?.id) {
    const groupPath = `/notes/${encodeURIComponent(snapshot.selectedGroup.id)}`;
    const entryId = snapshot?.editorState?.entry?.id || snapshot?.groupInitialEntryId;
    if (entryId) {
      return `${groupPath}/entries/${encodeURIComponent(entryId)}`;
    }
    return groupPath;
  }
  if (snapshot?.activeView === "learning-lesson" && snapshot?.learningLessonItemId) {
    return `/learning/lesson/${encodeURIComponent(snapshot.learningLessonItemId)}`;
  }
  if (snapshot?.activeView === "learning") {
    if (snapshot?.learningPageType === "library" && snapshot?.learningPageId) {
      return `/learning/library/${encodeURIComponent(snapshot.learningPageId)}`;
    }
    if (snapshot?.learningPageType === "course" && snapshot?.learningPageId) {
      const path = `/learning/course/${encodeURIComponent(snapshot.learningPageId)}`;
      const note = window.location.pathname === path ? new URLSearchParams(window.location.search).get("note") : null;
      return path + (note ? `?note=${encodeURIComponent(note)}` : "");
    }
    if (snapshot?.learningPageType === "program" && snapshot?.learningPageId) {
      const path = `/learning/program/${encodeURIComponent(snapshot.learningPageId)}`;
      const note = window.location.pathname === path ? new URLSearchParams(window.location.search).get("note") : null;
      return path + (note ? `?note=${encodeURIComponent(note)}` : "");
    }
    if (snapshot?.learningPageType === "module" && snapshot?.learningPageId) {
      return `/learning/module/${encodeURIComponent(snapshot.learningPageId)}`;
    }
    return "/learning";
  }
  return "/";
}

function readDraggedFolderPayload(event) {
  const rawPayload = event.dataTransfer?.getData(FOLDER_DRAG_MIME)
    || event.dataTransfer?.getData("text/plain")
    || "";
  if (!rawPayload) return null;

  try {
    const parsed = JSON.parse(rawPayload);
    if (parsed?.type !== "folder" || !parsed?.sourceGroupId || !parsed?.sourceFolderPath) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function formatTransferTimestamp(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function readNotesTransferStatus() {
  if (typeof window === "undefined") return { lastImportAt: "", lastExportAt: "" };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(NOTES_TRANSFER_STATUS_KEY) || "{}");
    return {
      lastImportAt: String(parsed?.lastImportAt || ""),
      lastExportAt: String(parsed?.lastExportAt || ""),
    };
  } catch {
    return { lastImportAt: "", lastExportAt: "" };
  }
}

function splitGroupsIntoChunks(groups) {
  const items = Array.isArray(groups) ? groups : [];
  if (!items.length) return [];

  const chunks = [];
  let currentChunk = [];
  let currentSize = 0;

  for (const group of items) {
    const serialized = JSON.stringify(group);
    if (
      currentChunk.length
      && currentSize + serialized.length > NOTES_IMPORT_CHUNK_CHAR_LIMIT
    ) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentSize = 0;
    }
    currentChunk.push(group);
    currentSize += serialized.length;
  }

  if (currentChunk.length) {
    chunks.push(currentChunk);
  }

  return chunks;
}

function filterImportableGroups(groups) {
  const items = Array.isArray(groups) ? groups : [];
  const importableGroups = items.filter((group) => {
    const entries = Array.isArray(group?.entries) ? group.entries : [];
    return entries.length > 0;
  });
  return {
    importableGroups,
    skippedEmptyTitleCount: Math.max(0, items.length - importableGroups.length),
  };
}

const DEFAULT_WEATHER_COORDS = {
  latitude: 40.7128,
  longitude: -74.0060,
  label: "New York fallback",
};
const ZIP_CODE_PATTERN = /^\d{5}(?:-\d{4})?$/;

function weatherCodeLabel(code, isDay) {
  const numericCode = Number(code);
  const daylight = Boolean(isDay);
  if (numericCode === 0) return daylight ? "Clear" : "Clear night";
  if (numericCode === 1) return daylight ? "Mostly clear" : "Mostly clear night";
  if (numericCode === 2) return "Partly cloudy";
  if (numericCode === 3) return "Overcast";
  if ([45, 48].includes(numericCode)) return "Fog";
  if ([51, 53, 55, 56, 57].includes(numericCode)) return "Drizzle";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(numericCode)) return "Rain";
  if ([71, 73, 75, 77, 85, 86].includes(numericCode)) return "Snow";
  if ([95, 96, 99].includes(numericCode)) return "Thunderstorm";
  return "Current conditions";
}

function toFahrenheit(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return Math.round((number * 9) / 5 + 32);
}

function formatCalendarEventTime(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

async function resolveWeatherLookup(query, signal) {
  const trimmedQuery = String(query || "").trim();
  if (!trimmedQuery) {
    throw new Error("Enter a ZIP code or city name.");
  }

  if (ZIP_CODE_PATTERN.test(trimmedQuery)) {
    const zipCode = trimmedQuery.slice(0, 5);
    const response = await window.fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zipCode)}`, { signal });
    if (!response.ok) {
      throw new Error("ZIP code was not found.");
    }
    const payload = await response.json();
    const place = Array.isArray(payload?.places) ? payload.places[0] : null;
    const latitude = Number(place?.latitude);
    const longitude = Number(place?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new Error("ZIP code weather lookup is unavailable.");
    }
    const placeName = String(place?.["place name"] || zipCode).trim();
    const stateCode = String(place?.["state abbreviation"] || "").trim();
    return {
      latitude,
      longitude,
      label: stateCode ? `${placeName}, ${stateCode}` : placeName,
    };
  }

  const params = new URLSearchParams({
    name: trimmedQuery,
    count: "1",
    language: "en",
    format: "json",
  });
  const response = await window.fetch(`https://geocoding-api.open-meteo.com/v1/search?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error("Weather location search is unavailable.");
  }
  const payload = await response.json();
  const match = Array.isArray(payload?.results) ? payload.results[0] : null;
  const latitude = Number(match?.latitude);
  const longitude = Number(match?.longitude);
  if (!match || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error("No matching weather location was found.");
  }

  const parts = [
    String(match.name || "").trim(),
    String(match.admin1 || "").trim(),
  ].filter(Boolean);
  return {
    latitude,
    longitude,
    label: parts.join(", "),
  };
}

export default function App() {
  const shareMatch = typeof window !== "undefined" ? window.location.pathname.match(/^\/share\/([^/]+)$/) : null;
  const shareToken = shareMatch?.[1] || "";
  const initialAppRoute = typeof window !== "undefined"
    ? parseAppPath(window.location.pathname)
    : getDefaultHistorySnapshot();
  // Keep the persistent sidebar on tablet and desktop screens. The drawer is
  // reserved for the same small-screen breakpoint used by the stylesheet.
  const mobileMediaQuery = "(max-width: 820px)";
  const [activeView, setActiveView] = useState(initialAppRoute.activeView);
  const [user, setUser] = useState(null);
  const profileRequestRef = useRef(0);
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [globalApiError, setGlobalApiError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(initialAppRoute.selectedGroup || null);
  const [editorState, setEditorState] = useState(null);
  const [groupRefreshToken, setGroupRefreshToken] = useState(0);
  const [groupInitialFolderPath, setGroupInitialFolderPath] = useState(initialAppRoute.groupInitialFolderPath || "");
  const [groupInitialEntryId, setGroupInitialEntryId] = useState(initialAppRoute.groupInitialEntryId || "");
  const [expandedSidebarGroupId, setExpandedSidebarGroupId] = useState("");
  const [learningPageType, setLearningPageType] = useState(initialAppRoute.learningPageType || "home");
  const [learningPageId, setLearningPageId] = useState(initialAppRoute.learningPageId || "");
  const [learningLessonItemId, setLearningLessonItemId] = useState(initialAppRoute.learningLessonItemId || "");
  const [folderCardDropTarget, setFolderCardDropTarget] = useState("");
  const [notesImporting, setNotesImporting] = useState(false);
  const [notesExporting, setNotesExporting] = useState(false);
  const [notesTransferMessage, setNotesTransferMessage] = useState("");
  const [notesTransferStatus, setNotesTransferStatus] = useState(() => readNotesTransferStatus());
  const [weatherSnapshot, setWeatherSnapshot] = useState({
    loading: false,
    error: "",
    label: "",
    timezone: "",
    updatedAt: "",
    temperatureF: "",
    apparentTemperatureF: "",
    windSpeedMph: "",
    humidity: "",
    precipitation: "",
    condition: "",
  });
  const [weatherQuery, setWeatherQuery] = useState("");
  const [weatherSearchMessage, setWeatherSearchMessage] = useState("");
  const [todayEventsSnapshot, setTodayEventsSnapshot] = useState({
    loading: false,
    error: "",
    events: [],
  });
  const [charterNewsSnapshot, setCharterNewsSnapshot] = useState({
    loading: false,
    error: "",
    items: [],
  });
  const [dashboardLessonsSnapshot, setDashboardLessonsSnapshot] = useState({
    loading: false,
    error: "",
    items: [],
  });
  const notesImportInputRef = useRef(null);
  const todayLabel = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date());
  const {
    sidebarCollapsed,
    isMobileSidebar,
    sidebarPanelRef,
    mobileMenuButtonRef,
    openSidebar,
    closeSidebar,
    toggleSidebar,
  } = useSidebarDrawer({
    mobileMediaQuery,
    storageKey: SIDEBAR_PREF_KEY,
  });
  const isDashboardMenuHidden = activeView === "dashboard" && !isMobileSidebar && !dashboardMenuOpen;

  useEffect(() => {
    if (activeView !== "dashboard") setDashboardMenuOpen(false);
  }, [activeView]);
  const {
    titles,
    total,
    offset,
    searchInput,
    searchTerm,
    searchSuggestions,
    showSuggestions,
    loadingSuggestions,
    loadingTitles,
    titlesError,
    setOffset,
    setSearchInput,
    setSearchTerm,
    setSearchSuggestions,
    setShowSuggestions,
    fetchTitles,
    resetTitleLibrary,
    optimisticCreateTitle,
    replaceTempTitle,
    revertTempTitle,
  } = useTitleLibrary({
    api,
    user,
    pageSize: PAGE_SIZE,
  });
  const {
    sidebarChildrenByGroup,
    sidebarLoadingGroupId,
    refreshSidebarChildren,
    invalidateSidebarChildren,
    syncSidebarChildrenForGroups,
    resetSidebarChildrenCache,
  } = useSidebarChildrenCache({
    api,
    selectedGroupId: selectedGroup?.id || "",
    expandedGroupId: expandedSidebarGroupId,
  });
  const initialHistorySnapshotRef = useRef(null);
  const historyReadyRef = useRef(false);
  const historyRestoringRef = useRef(false);
  const historySnapshotRef = useRef("");
  const weatherRequestIdRef = useRef(0);

  if (!initialHistorySnapshotRef.current) {
    initialHistorySnapshotRef.current = buildHistorySnapshot({
      activeView,
      selectedGroup,
      editorState,
      groupInitialFolderPath,
      groupInitialEntryId,
      expandedSidebarGroupId,
      learningPageType,
      learningPageId,
      learningLessonItemId,
    });
  }

  const SelectedLegacyView = typeof activeView === "string" ? legacyViewComponents[activeView] || null : null;

  const applyHistorySnapshot = (snapshot) => {
    setActiveView(snapshot?.activeView || "dashboard");
    setSelectedGroup(snapshot?.selectedGroup || null);
    setEditorState(snapshot?.editorState || null);
    setGroupInitialFolderPath(snapshot?.groupInitialFolderPath || "");
    setGroupInitialEntryId(snapshot?.groupInitialEntryId || "");
    setExpandedSidebarGroupId(snapshot?.expandedSidebarGroupId || "");
    setLearningPageType(snapshot?.learningPageType || "home");
    setLearningPageId(snapshot?.learningPageId || "");
    setLearningLessonItemId(snapshot?.learningLessonItemId || "");
    setGroupRefreshToken(0);
  };

  const fetchProfile = async () => {
    const requestId = ++profileRequestRef.current;
    try {
      const res = await api.get("/api/auth/me");
      if (requestId !== profileRequestRef.current) return null;
      setUser(res.data.user || null);
      return res.data.user || null;
    } catch {
      if (requestId !== profileRequestRef.current) return null;
      setUser(null);
      return null;
    } finally {
      if (requestId === profileRequestRef.current) setProfileLoading(false);
    }
  };
  useEffect(() => {
    if (shareToken) {
      setProfileLoading(false);
      return;
    }
    fetchProfile();
  }, [shareToken]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let clearTimer = null;
    const handleApiError = (event) => {
      const detail = event?.detail || {};
      setGlobalApiError(detail);
      if (clearTimer) {
        window.clearTimeout(clearTimer);
      }
      clearTimer = window.setTimeout(() => {
        setGlobalApiError(null);
      }, detail.kind === "auth" ? 12000 : 7000);
    };

    window.addEventListener("app:api-error", handleApiError);
    return () => {
      if (clearTimer) {
        window.clearTimeout(clearTimer);
      }
      window.removeEventListener("app:api-error", handleApiError);
    };
  }, []);

  useEffect(() => {
    if (shareToken || typeof window === "undefined") {
      return undefined;
    }

    const existingSnapshot = window.history.state?.[APP_HISTORY_KEY]
      ? window.history.state
      : initialHistorySnapshotRef.current;
    const serializedSnapshot = JSON.stringify(existingSnapshot);
    historySnapshotRef.current = serializedSnapshot;
    historyReadyRef.current = true;
    window.history.replaceState(existingSnapshot, "", buildAppPath(existingSnapshot));

    if (window.history.state?.[APP_HISTORY_KEY]) {
      applyHistorySnapshot(window.history.state);
    }

    const handlePopState = (event) => {
      const nextSnapshot = event.state?.[APP_HISTORY_KEY]
        ? event.state
        : getDefaultHistorySnapshot();

      historyRestoringRef.current = true;
      historySnapshotRef.current = JSON.stringify(nextSnapshot);
      applyHistorySnapshot(nextSnapshot);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [shareToken]);

  useEffect(() => {
    if (!user || activeView !== "dashboard") return undefined;
    if (typeof window === "undefined" || typeof window.fetch !== "function") return undefined;

    let cancelled = false;
    const abortController = new AbortController();
    const requestId = weatherRequestIdRef.current + 1;
    weatherRequestIdRef.current = requestId;

    async function loadWeather(coords) {
      try {
        setWeatherSearchMessage("");
        setWeatherSnapshot((current) => ({
          ...current,
          loading: true,
          error: "",
          label: coords.label,
        }));

        const params = new URLSearchParams({
          latitude: String(coords.latitude),
          longitude: String(coords.longitude),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "auto",
          current: [
            "temperature_2m",
            "apparent_temperature",
            "relative_humidity_2m",
            "precipitation",
            "weather_code",
            "wind_speed_10m",
            "is_day",
          ].join(","),
        });
        const response = await window.fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`, {
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error("Weather service is unavailable");
        }

        const payload = await response.json();
        if (cancelled || weatherRequestIdRef.current !== requestId) return;

        const current = payload?.current || {};
        const windSpeed = Number(current.wind_speed_10m);
        setWeatherSnapshot({
          loading: false,
          error: "",
          label: coords.label,
          timezone: payload?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          updatedAt: String(current.time || ""),
          temperatureF: toFahrenheit(current.temperature_2m),
          apparentTemperatureF: toFahrenheit(current.apparent_temperature),
          windSpeedMph: Number.isFinite(windSpeed) ? Math.round(windSpeed * 0.621371) : "",
          humidity: Number.isFinite(Number(current.relative_humidity_2m)) ? Math.round(Number(current.relative_humidity_2m)) : "",
          precipitation: Number.isFinite(Number(current.precipitation)) ? Number(current.precipitation).toFixed(2) : "",
          condition: weatherCodeLabel(current.weather_code, current.is_day),
        });
      } catch (error) {
        if (cancelled || error?.name === "AbortError" || weatherRequestIdRef.current !== requestId) return;
        setWeatherSnapshot({
          loading: false,
          error: "Unable to load local weather right now.",
          label: coords.label,
          timezone: "",
          updatedAt: "",
          temperatureF: "",
          apparentTemperatureF: "",
          windSpeedMph: "",
          humidity: "",
          precipitation: "",
          condition: "",
        });
      }
    }

    const loadFallbackWeather = () => loadWeather(DEFAULT_WEATHER_COORDS);

    if (window.navigator?.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          loadWeather({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            label: "Browser location",
          });
        },
        () => loadFallbackWeather(),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 1800000 },
      );
    } else {
      loadFallbackWeather();
    }

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [activeView, user]);

  useEffect(() => {
    if (!user || activeView !== "dashboard") return undefined;
    if (process.env.NODE_ENV === "test") return undefined;

    let cancelled = false;

    async function loadCharterNews() {
      try {
        setCharterNewsSnapshot({
          loading: true,
          error: "",
          items: [],
        });
        const res = await api.get("/api/dashboard/charter-news");
        if (cancelled) return;
        setCharterNewsSnapshot({
          loading: false,
          error: "",
          items: Array.isArray(res.data?.items) ? res.data.items : [],
        });
      } catch (error) {
        if (cancelled) return;
        setCharterNewsSnapshot({
          loading: false,
          error: error.response?.data?.error || "Unable to load Charter Communications news.",
          items: [],
        });
      }
    }

    loadCharterNews();
    return () => {
      cancelled = true;
    };
  }, [activeView, user]);

  useEffect(() => {
    if (!user || activeView !== "dashboard") return undefined;
    if (process.env.NODE_ENV === "test") return undefined;

    let cancelled = false;

    async function loadDashboardLessons() {
      try {
        setDashboardLessonsSnapshot({
          loading: true,
          error: "",
          items: [],
        });
        const res = await api.get("/api/learning/items");
        if (cancelled) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        const prioritized = items
          .filter((item) => item.itemType === "lesson")
          .sort((left, right) => {
            const leftTime = new Date(left.lastStudiedAt || left.lastActivityAt || left.updatedAt || left.createdAt || 0).getTime();
            const rightTime = new Date(right.lastStudiedAt || right.lastActivityAt || right.updatedAt || right.createdAt || 0).getTime();
            return rightTime - leftTime;
          })
          .slice(0, 4);

        setDashboardLessonsSnapshot({
          loading: false,
          error: "",
          items: prioritized,
        });
      } catch (error) {
        if (cancelled) return;
        setDashboardLessonsSnapshot({
          loading: false,
          error: error.response?.data?.error || "Unable to load recent lessons.",
          items: [],
        });
      }
    }

    loadDashboardLessons();
    return () => {
      cancelled = true;
    };
  }, [activeView, user]);

  useEffect(() => {
    if (!user || activeView !== "dashboard") return undefined;
    if (process.env.NODE_ENV === "test") return undefined;

    let cancelled = false;
    const today = new Date();
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const nextDayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 0, 0, 0, 0);

    async function loadTodayEvents() {
      try {
        setTodayEventsSnapshot({
          loading: true,
          error: "",
          events: [],
        });

        const res = await api.get(
          `/api/calendar/events?from=${encodeURIComponent(dayStart.toISOString())}&to=${encodeURIComponent(nextDayStart.toISOString())}`
        );
        if (cancelled) return;
        const events = Array.isArray(res.data?.events) ? res.data.events : [];
        const sortedEvents = [...events].sort(
          (left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime()
        );
        setTodayEventsSnapshot({
          loading: false,
          error: "",
          events: sortedEvents,
        });
      } catch (error) {
        if (cancelled) return;
        setTodayEventsSnapshot({
          loading: false,
          error: error.response?.data?.error || "Unable to load today's calendar events.",
          events: [],
        });
      }
    }

    loadTodayEvents();
    return () => {
      cancelled = true;
    };
  }, [activeView, user]);

  useEffect(() => {
    if (!historyReadyRef.current || shareToken || typeof window === "undefined") {
      return;
    }

    const nextSnapshot = buildHistorySnapshot({
      activeView,
      selectedGroup,
      editorState,
      groupInitialFolderPath,
      groupInitialEntryId,
      expandedSidebarGroupId,
      learningPageType,
      learningPageId,
      learningLessonItemId,
    });
    const serializedSnapshot = JSON.stringify(nextSnapshot);

    if (historyRestoringRef.current) {
      historyRestoringRef.current = false;
      historySnapshotRef.current = serializedSnapshot;
      return;
    }

    if (serializedSnapshot === historySnapshotRef.current) {
      return;
    }

    historySnapshotRef.current = serializedSnapshot;
    window.history.pushState(nextSnapshot, "", buildAppPath(nextSnapshot));
  }, [
    activeView,
    editorState,
    expandedSidebarGroupId,
    groupInitialEntryId,
    groupInitialFolderPath,
    learningLessonItemId,
    learningPageId,
    learningPageType,
    selectedGroup,
    shareToken,
  ]);

  const loadWeatherForCoords = async (coords, options = {}) => {
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;

    const requestId = weatherRequestIdRef.current + 1;
    weatherRequestIdRef.current = requestId;
    const abortController = new AbortController();
    const signal = options.signal || abortController.signal;

    try {
      setWeatherSearchMessage("");
      setWeatherSnapshot((current) => ({
        ...current,
        loading: true,
        error: "",
        label: coords.label,
      }));

      const params = new URLSearchParams({
        latitude: String(coords.latitude),
        longitude: String(coords.longitude),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "auto",
        current: [
          "temperature_2m",
          "apparent_temperature",
          "relative_humidity_2m",
          "precipitation",
          "weather_code",
          "wind_speed_10m",
          "is_day",
        ].join(","),
      });

      const response = await window.fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`, { signal });
      if (!response.ok) {
        throw new Error("Weather service is unavailable");
      }

      const payload = await response.json();
      if (weatherRequestIdRef.current !== requestId) return;

      const current = payload?.current || {};
      const windSpeed = Number(current.wind_speed_10m);
      setWeatherSnapshot({
        loading: false,
        error: "",
        label: coords.label,
        timezone: payload?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        updatedAt: String(current.time || ""),
        temperatureF: toFahrenheit(current.temperature_2m),
        apparentTemperatureF: toFahrenheit(current.apparent_temperature),
        windSpeedMph: Number.isFinite(windSpeed) ? Math.round(windSpeed * 0.621371) : "",
        humidity: Number.isFinite(Number(current.relative_humidity_2m)) ? Math.round(Number(current.relative_humidity_2m)) : "",
        precipitation: Number.isFinite(Number(current.precipitation)) ? Number(current.precipitation).toFixed(2) : "",
        condition: weatherCodeLabel(current.weather_code, current.is_day),
      });
    } catch (error) {
      if (error?.name === "AbortError" || weatherRequestIdRef.current !== requestId) return;
      setWeatherSnapshot({
        loading: false,
        error: options.errorMessage || "Unable to load local weather right now.",
        label: coords.label,
        timezone: "",
        updatedAt: "",
        temperatureF: "",
        apparentTemperatureF: "",
        windSpeedMph: "",
        humidity: "",
        precipitation: "",
        condition: "",
      });
    }
  };

  const handleUseCurrentWeatherLocation = () => {
    if (typeof window === "undefined") return;
    setWeatherSearchMessage("");
    if (window.navigator?.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          loadWeatherForCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            label: "Browser location",
          });
        },
        () => {
          setWeatherSearchMessage("Using New York fallback because browser location was unavailable.");
          loadWeatherForCoords(DEFAULT_WEATHER_COORDS, { errorMessage: "Unable to load local weather right now." });
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 1800000 },
      );
      return;
    }

    setWeatherSearchMessage("Browser location is unavailable. Showing New York fallback.");
    loadWeatherForCoords(DEFAULT_WEATHER_COORDS, { errorMessage: "Unable to load local weather right now." });
  };

  const handleWeatherSearchSubmit = async (event) => {
    event.preventDefault();
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;

    try {
      setWeatherSearchMessage("");
      const coords = await resolveWeatherLookup(weatherQuery, undefined);
      await loadWeatherForCoords(coords, { errorMessage: "Unable to load weather for this search." });
    } catch (error) {
      setWeatherSearchMessage(error.message || "Weather search could not be completed.");
    }
  };

  const handleLogout = async () => {
    ++profileRequestRef.current;
    setProfileLoading(true);
    try {
      await api.post("/api/logout");
    } catch {
      setProfileLoading(false);
      setGlobalApiError({ kind: "auth", message: "Sign out could not complete. Please try again." });
      return;
    }
    try { window.localStorage.setItem("addanote:logout", String(Date.now())); } catch {}
    setProfileLoading(false);
    setUser(null);
    resetTitleLibrary();
    setSelectedGroup(null);
    setEditorState(null);
    setGroupInitialFolderPath("");
    setGroupInitialEntryId("");
    setLearningLessonItemId("");
    closeSidebar();
    setExpandedSidebarGroupId("");
    resetSidebarChildrenCache();
    setActiveView("dashboard");
  };

  const handleLoginSuccess = async () => {
    setProfileLoading(true);
    await fetchProfile();
    resetTitleLibrary();
    setSelectedGroup(initialAppRoute.selectedGroup || null);
    setEditorState(null);
    setGroupInitialFolderPath(initialAppRoute.groupInitialFolderPath || "");
    setGroupInitialEntryId(initialAppRoute.groupInitialEntryId || "");
    setLearningPageType(initialAppRoute.learningPageType || "home");
    setLearningPageId(initialAppRoute.learningPageId || "");
    setLearningLessonItemId(initialAppRoute.learningLessonItemId || "");
    closeSidebar();
    setExpandedSidebarGroupId("");
    resetSidebarChildrenCache();
    setActiveView(["login", "welcome"].includes(initialAppRoute.activeView) ? "dashboard" : (initialAppRoute.activeView || "dashboard"));
  };

  const handleOptimisticCreate = (tempGroup) => {
    optimisticCreateTitle(tempGroup);
  };

  const handleReplaceTemp = (tempId, serverGroup) => {
    replaceTempTitle(tempId, serverGroup);
  };

  const handleRevertTemp = (tempId) => {
    revertTempTitle(tempId);
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this entire title and all notes inside it?")) return;
    try {
      await api.delete(`/api/notes/${id}`);
      invalidateSidebarChildren([id]);
      const isLastItemOnPage = titles.length === 1 && offset > 0;
      const nextOffset = isLastItemOnPage ? Math.max(0, offset - PAGE_SIZE) : offset;
      if (nextOffset !== offset) {
        setOffset(nextOffset);
      } else {
        fetchTitles({ nextOffset });
      }
    } catch (err) {
      window.alert(err.response?.data?.error || "Failed to delete title.");
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setShowSuggestions(false);
    setOffset(0);
    setSearchTerm(searchInput.trim());
    if (user) {
      fetchTitles({ nextOffset: 0, nextSearch: searchInput.trim() });
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setOffset(0);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchInput(suggestion.title || "");
    setSearchTerm(suggestion.title || "");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setOffset(0);
  };

  const openGroup = (group) => {
    setSelectedGroup(group);
    setEditorState(null);
    setGroupInitialFolderPath("");
    setGroupInitialEntryId("");
    setActiveView("group");
    closeSidebar();
  };

  const handleSidebarToggleGroup = async (group) => {
    const nextGroupId = expandedSidebarGroupId === group.id ? "" : group.id;
    setExpandedSidebarGroupId(nextGroupId);
    if (nextGroupId) {
      await refreshSidebarChildren(group.id);
    }
  };

  const handleSidebarOpenChild = (group, entry) => {
    setSelectedGroup(group);
    setEditorState(null);
    setGroupInitialFolderPath(entry?.folder_path || "");
    setGroupInitialEntryId(entry?.id || "");
    setActiveView("group");
    closeSidebar();
  };

  const handleSidebarFolderDrop = async (payload, targetGroup, targetFolderPath = "") => {
    if (!payload?.sourceGroupId || !payload?.sourceFolderPath || !targetGroup?.id) return;

    try {
      await api.post(`/api/notes/${payload.sourceGroupId}/folders/move`, {
        sourceFolderPath: payload.sourceFolderPath,
        targetGroupId: targetGroup.id,
        targetFolderPath,
      });

      fetchTitles();
      const affectedGroupIds = [...new Set([payload.sourceGroupId, targetGroup.id])];
      invalidateSidebarChildren(affectedGroupIds);
      await syncSidebarChildrenForGroups(affectedGroupIds);

      if (selectedGroup?.id && affectedGroupIds.includes(selectedGroup.id)) {
        setGroupInitialFolderPath("");
        setGroupInitialEntryId("");
        setGroupRefreshToken((current) => current + 1);
      }

      setExpandedSidebarGroupId(targetGroup.id);
    } catch (err) {
      window.alert(err.response?.data?.error || "Failed to move folder.");
    }
  };

  const handleTitleCardFolderDragOver = (event, groupId) => {
    const payload = readDraggedFolderPayload(event);
    if (!payload || !groupId) return;
    event.preventDefault();
    if (folderCardDropTarget !== groupId) {
      setFolderCardDropTarget(groupId);
    }
  };

  const handleTitleCardFolderDrop = async (event, group) => {
    const payload = readDraggedFolderPayload(event);
    if (!payload || !group?.id) return;
    event.preventDefault();
    event.stopPropagation();
    setFolderCardDropTarget("");
    await handleSidebarFolderDrop(payload, group, "");
  };

  const handleMenuViewChange = (nextView) => {
    setEditorState(null);
    if (nextView !== "learning-lesson") {
      setLearningLessonItemId("");
    }
    if (nextView !== "learning" && nextView !== "learning-lesson") {
      setLearningPageType("home");
      setLearningPageId("");
    }
    if (nextView === "generator" && !user?.isAdmin) {
      setActiveView("dashboard");
      closeSidebar();
      return;
    }
    if (nextView === "new") {
      setActiveView("new-group");
    } else if (nextView === "learning") {
      setLearningPageType("home");
      setLearningPageId("");
      setActiveView("learning");
    } else {
      setActiveView(nextView);
    }
    closeSidebar();
  };

  const handleGoHomeFromMenu = () => {
    goHome();
    closeSidebar();
  };

  const goHome = () => {
    setSelectedGroup(null);
    setEditorState(null);
    setGroupRefreshToken(0);
    setGroupInitialFolderPath("");
    setGroupInitialEntryId("");
    setLearningPageType("home");
    setLearningPageId("");
    setLearningLessonItemId("");
    setActiveView("dashboard");
    if (user) {
      fetchTitles({ nextOffset: 0 });
      setOffset(0);
    }
  };

  const openLearningRoute = (nextType = "home", nextId = "") => {
    setSelectedGroup(null);
    setEditorState(null);
    setGroupInitialFolderPath("");
    setGroupInitialEntryId("");
    setLearningLessonItemId("");
    setLearningPageType(nextType);
    setLearningPageId(nextId || "");
    setActiveView("learning");
    closeSidebar();
  };

  const openLearningLesson = (itemId, parentType = learningPageType, parentId = learningPageId) => {
    setSelectedGroup(null);
    setEditorState(null);
    setGroupInitialFolderPath("");
    setGroupInitialEntryId("");
    setLearningPageType(parentType || "home");
    setLearningPageId(parentId || "");
    setLearningLessonItemId(itemId);
    setActiveView("learning-lesson");
    closeSidebar();
  };

  const updateNotesTransferStatus = (nextStatus) => {
    setNotesTransferStatus(nextStatus);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(NOTES_TRANSFER_STATUS_KEY, JSON.stringify(nextStatus));
    }
  };

  const handleExportAllNotes = async () => {
    setNotesExporting(true);
    setNotesTransferMessage("");
    try {
      const res = await api.get("/api/notes/export-all");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `addanote-notes-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      updateNotesTransferStatus({
        ...notesTransferStatus,
        lastExportAt: new Date().toISOString(),
      });
    } catch (err) {
      setNotesTransferMessage(err.response?.data?.error || "Failed to export notes.");
    } finally {
      setNotesExporting(false);
    }
  };

  const handleImportAllNotes = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setNotesImporting(true);
    setNotesTransferMessage("");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const groups = Array.isArray(parsed?.groups) ? parsed.groups : [];
      if (!groups.length) {
        throw new Error("Backup file does not contain any titles.");
      }
      const { importableGroups, skippedEmptyTitleCount } = filterImportableGroups(groups);
      if (!importableGroups.length) {
        throw new Error("Backup file does not contain any titles with notes.");
      }
      const chunks = splitGroupsIntoChunks(importableGroups);
      let titleCount = 0;
      let noteCount = 0;
      let duplicateTitleCount = 0;
      let duplicateNoteCount = 0;
      let newTitleCount = 0;
      let newNoteCount = 0;
      let splitSourceNoteCount = 0;
      for (const chunk of chunks) {
        const previewRes = await api.post("/api/notes/import-all-preview", { groups: chunk });
        titleCount += Number(previewRes.data?.titleCount || 0);
        noteCount += Number(previewRes.data?.noteCount || 0);
        duplicateTitleCount += Number(previewRes.data?.duplicateTitleCount || 0);
        duplicateNoteCount += Number(previewRes.data?.duplicateNoteCount || 0);
        newTitleCount += Number(previewRes.data?.newTitleCount || 0);
        newNoteCount += Number(previewRes.data?.newNoteCount || 0);
        splitSourceNoteCount += Number(previewRes.data?.splitSourceNoteCount || 0);
      }
      const nothingNewMessage = newTitleCount === 0 && newNoteCount === 0
        ? "\nNo new titles or notes will be imported."
        : "";
      const previewMessage = `Import preview:\n\n${titleCount} title${titleCount === 1 ? "" : "s"} found.\n${noteCount} note${noteCount === 1 ? "" : "s"} found.\n${newTitleCount} new title${newTitleCount === 1 ? "" : "s"}, ${duplicateTitleCount} likely duplicate${duplicateTitleCount === 1 ? "" : "s"}.\n${newNoteCount} new note${newNoteCount === 1 ? "" : "s"}, ${duplicateNoteCount} likely duplicate${duplicateNoteCount === 1 ? "" : "s"}.${nothingNewMessage}\nDuplicate titles and notes will be skipped automatically.\n${skippedEmptyTitleCount} empty title${skippedEmptyTitleCount === 1 ? "" : "s"} will be skipped.${splitSourceNoteCount ? `\n${splitSourceNoteCount} oversized note${splitSourceNoteCount === 1 ? "" : "s"} will be split into multiple notes.` : ""}\n\nContinue with import?`;
      const confirmedWithSplits = window.confirm(previewMessage);
      if (!confirmedWithSplits) {
        setNotesTransferMessage("Import canceled.");
        return;
      }
      let importedTitleCount = 0;
      let importedNoteCount = 0;
      let skippedImportedDuplicateTitleCount = 0;
      let skippedImportedDuplicateNoteCount = 0;
      for (const chunk of chunks) {
        const res = await api.post("/api/notes/import-all", { groups: chunk });
        importedTitleCount += Number(res.data?.titleCount || 0);
        importedNoteCount += Number(res.data?.noteCount || 0);
        skippedImportedDuplicateTitleCount += Number(res.data?.skippedDuplicateTitleCount || 0);
        skippedImportedDuplicateNoteCount += Number(res.data?.skippedDuplicateNoteCount || 0);
      }
      fetchTitles({ nextOffset: 0 });
      setOffset(0);
      setNotesTransferMessage(
        `Imported ${importedTitleCount} title${importedTitleCount === 1 ? "" : "s"} and ${importedNoteCount} note${importedNoteCount === 1 ? "" : "s"}.`
        + (skippedImportedDuplicateTitleCount || skippedImportedDuplicateNoteCount
          ? ` Skipped ${skippedImportedDuplicateTitleCount} duplicate title${skippedImportedDuplicateTitleCount === 1 ? "" : "s"} and ${skippedImportedDuplicateNoteCount} duplicate note${skippedImportedDuplicateNoteCount === 1 ? "" : "s"}.`
          : "")
        + (skippedEmptyTitleCount ? ` Skipped ${skippedEmptyTitleCount} empty title${skippedEmptyTitleCount === 1 ? "" : "s"}.` : ""),
      );
      updateNotesTransferStatus({
        ...notesTransferStatus,
        lastImportAt: new Date().toISOString(),
      });
    } catch (err) {
      setNotesTransferMessage(err.response?.data?.error || err.message || "Failed to import notes.");
    } finally {
      setNotesImporting(false);
    }
  };

  useEffect(() => {
    if (!profileLoading && user && activeView === "login") setActiveView("dashboard");
  }, [profileLoading, user, activeView]);

  useEffect(() => {
    if (shareToken) return undefined;
    const clearPrivateScreen = () => {
      ++profileRequestRef.current;
      setUser(null);
      resetTitleLibrary();
      setSelectedGroup(null);
      setEditorState(null);
      setGroupInitialFolderPath("");
      setGroupInitialEntryId("");
      setLearningLessonItemId("");
      resetSidebarChildrenCache();
    };
    const verifySession = () => {
      flushSync(() => setProfileLoading(true));
      fetchProfile();
    };
    const handlePageHide = () => {
      // Strip private DOM before the browser captures a history-cache snapshot.
      flushSync(() => { clearPrivateScreen(); setProfileLoading(true); });
    };
    const handlePageShow = (event) => { if (event.persisted) verifySession(); };
    const handleStorage = (event) => {
      if (event.key !== "addanote:logout") return;
      flushSync(() => { clearPrivateScreen(); setProfileLoading(false); setActiveView("dashboard"); });
    };
    const handleVisibility = () => { if (document.visibilityState === "visible") verifySession(); };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", verifySession);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", verifySession);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [shareToken]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const recentTitles = titles.slice(0, 6);

  if (profileLoading) {
    return <div className="app-loading">Loading AddaNote...</div>;
  }

  if (shareToken) {
    return <PublicShareView token={shareToken} />;
  }

  if (!user || activeView === "welcome" || activeView === "login") {
    return <PublicLanding authOnly={activeView === "login"} user={user} onLogout={handleLogout} onLoginSuccess={async () => {
      await handleLoginSuccess();

    }} />;
  }
  return (
    <div className={`app-shell ${(sidebarCollapsed && !isMobileSidebar) || isDashboardMenuHidden ? "is-desktop-collapsed" : ""}`}>
      {globalApiError ? (
        <div className={`global-api-banner is-${globalApiError.kind || "default"}`} role="status" aria-live="polite">
          <span>{globalApiError.message || "Request failed."}</span>
          <button type="button" className="action-link" onClick={() => setGlobalApiError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {isMobileSidebar && !sidebarCollapsed ? (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close sidebar"
          onClick={closeSidebar}
        />
      ) : null}

      <aside
        ref={sidebarPanelRef}
        className={`sidebar-panel ${isMobileSidebar ? "is-mobile" : ""} ${!sidebarCollapsed ? "is-open" : ""} ${(sidebarCollapsed && !isMobileSidebar) || isDashboardMenuHidden ? "is-desktop-hidden" : ""}`}
        tabIndex={isMobileSidebar && !sidebarCollapsed ? -1 : undefined}
        role={isMobileSidebar ? "dialog" : undefined}
        aria-modal={isMobileSidebar && !sidebarCollapsed ? "true" : undefined}
        aria-label={isMobileSidebar ? "Navigation menu" : undefined}
      >
        <Menu
          user={user}
          total={total}
          insights={{
            totalTitles: total,
            currentPage,
            totalPages,
            activeFilter: searchTerm,
          }}
          activeView={activeView === "new-group" ? "new" : activeView}
          titles={titles}
          selectedGroupId={selectedGroup?.id || ""}
          expandedGroupId={expandedSidebarGroupId}
          sidebarChildrenByGroup={sidebarChildrenByGroup}
          loadingGroupId={sidebarLoadingGroupId}
          isCollapsed={sidebarCollapsed}
          isMobile={isMobileSidebar}
          onToggleCollapsed={() => {
            if (activeView === "dashboard") setDashboardMenuOpen(false);
            toggleSidebar();
          }}
          onToggleGroup={handleSidebarToggleGroup}
          onOpenChild={handleSidebarOpenChild}
          onDropFolder={handleSidebarFolderDrop}
          onLogout={handleLogout}
          onGoHome={() => {
            setDashboardMenuOpen(false);
            handleGoHomeFromMenu();
          }}
          setActiveView={(view) => {
            if (view === "dashboard") setDashboardMenuOpen(false);
            handleMenuViewChange(view);
          }}
          onCreateNote={() => {
            setEditorState({ mode: "create-group" });
            setActiveView("new-group");
          }}
        />
      </aside>

      <main className={`workspace-panel${activeView === "dashboard" ? " workspace-panel-dashboard" : activeView === "learning" ? " workspace-panel-learning" : ["calendar", "flights", "new-group", "entry-editor"].includes(activeView) ? " workspace-panel-spaced" : ""}`}>
        {(isMobileSidebar || sidebarCollapsed || isDashboardMenuHidden) && activeView !== "titles" ? (
          <div className="mobile-menu-row">
            <button
              type="button"
              className="mobile-menu-button"
              ref={mobileMenuButtonRef}
              onClick={() => {
                setDashboardMenuOpen(true);
                openSidebar();
              }}
              aria-label="Open menu"
            >
              <span className="icon-button-content">
                <HamburgerIcon />
              </span>
            </button>
          </div>
        ) : null}

        {activeView === "dashboard" && <a className="action-link" href="/welcome">About AddaNote ↗</a>}{activeView === "dashboard" && (
          <>
            <header className="dashboard-header dashboard-header-compact">
              <div><span className="eyebrow">Your workspace</span><h1>Dashboard</h1><p>Pick up where you left off.</p></div>
              <button className="action-button" onClick={() => { setEditorState({ mode: "create-group" }); setActiveView("new-group"); }}>New note</button>
            </header>
            <form className="dashboard-search" onSubmit={(event) => { handleSearchSubmit(event); setActiveView("titles"); }}>
              <label htmlFor="dashboard-search-input">Search titles and notes</label>
              <div><input id="dashboard-search-input" type="search" className="form-control" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Find a title or note…" /><button className="action-button" type="submit">Search</button></div>
            </form>
            <section className="dashboard-bottom-grid">
              <section className="dashboard-panel dashboard-learning-panel">
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Learning</span>
                    <h2>Continue learning</h2>
                  </div>
                  <button className="action-link" onClick={() => openLearningRoute("home", "")}>Open learning</button>
                </div>

                {dashboardLessonsSnapshot.loading ? <div className="feedback-panel">Loading recent lessons...</div> : null}
                {!dashboardLessonsSnapshot.loading && dashboardLessonsSnapshot.error ? (
                  <div className="feedback-panel error">{dashboardLessonsSnapshot.error}</div>
                ) : null}
                {!dashboardLessonsSnapshot.loading && !dashboardLessonsSnapshot.error && !dashboardLessonsSnapshot.items.length ? (
                  <div className="dashboard-calendar-empty">
                    <strong>No lesson cards yet</strong>
                    <span>Create or generate lessons from Learning to pin them here automatically.</span>
                  </div>
                ) : null}
                {!dashboardLessonsSnapshot.loading && !dashboardLessonsSnapshot.error && !!dashboardLessonsSnapshot.items.length ? (
                  <div className="dashboard-learning-list">
                    {dashboardLessonsSnapshot.items.map((lessonItem) => (
                      <article key={lessonItem.id} className="dashboard-learning-item">
                        <div className="dashboard-learning-item-top">
                          <div>
                            <strong>{lessonItem.title || "Untitled lesson"}</strong>
                            <span>{lessonItem.programTitle || lessonItem.certification || "Learning lesson"}</span>
                          </div>
                          <span className="dashboard-learning-status">{String(lessonItem.status || "not_started").replace(/_/g, " ")}</span>
                        </div>
                        <p className="dashboard-learning-item-copy">
                          {(lessonItem.summary || lessonItem.topicTitle || lessonItem.domainTitle || "Open this lesson to continue learning.").slice(0, 180)}
                        </p>
                        <div className="dashboard-learning-item-meta">
                          <span>{lessonItem.moduleTitle || "General module"}</span>
                          <span>{lessonItem.difficulty || "intermediate"}</span>
                          <span>{formatDate(lessonItem.lastStudiedAt || lessonItem.updatedAt || lessonItem.createdAt)}</span>
                        </div>
                        <div className="dashboard-actions">
                          {lessonItem.hasLessonContent ? (
                            <button
                              type="button"
                              className="action-button"
                              onClick={() => openLearningLesson(lessonItem.id, "module", lessonItem.moduleId || "")}
                            >
                              {lessonItem.status === "completed" ? "Review lesson" : "Continue lesson"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="action-button action-button-secondary"
                            onClick={() => openLearningRoute(lessonItem.moduleId ? "module" : "home", lessonItem.moduleId || "")}
                          >
                            {lessonItem.moduleId ? "Open module" : "Open learning"}
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : null}
              </section>

              <section className="dashboard-panel dashboard-calendar-panel">
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">Today</span>
                    <h2>Calendar events</h2>
                  </div>
                  <button className="action-link" onClick={() => setActiveView("calendar")}>Open calendar</button>
                </div>

                {todayEventsSnapshot.loading ? <div className="feedback-panel">Loading today's events...</div> : null}
                {!todayEventsSnapshot.loading && todayEventsSnapshot.error ? (
                  <div className="feedback-panel error">{todayEventsSnapshot.error}</div>
                ) : null}
                {!todayEventsSnapshot.loading && !todayEventsSnapshot.error && !todayEventsSnapshot.events.length ? (
                  <div className="dashboard-calendar-empty">
                    <strong>No events today</strong>
                    <span>Scheduled items for {todayLabel} will appear here.</span>
                    <button className="action-button action-button-secondary" onClick={() => setActiveView("calendar")}>Add event</button>
                  </div>
                ) : null}
                {!todayEventsSnapshot.loading && !todayEventsSnapshot.error && !!todayEventsSnapshot.events.length ? (
                  <div className="dashboard-calendar-list">
                    {todayEventsSnapshot.events.slice(0, 4).map((event) => (
                      <button
                        key={event.occurrenceId || event.id}
                        type="button"
                        className="dashboard-calendar-item"
                        onClick={() => setActiveView("calendar")}
                      >
                        <div className="dashboard-calendar-item-top">
                          <strong>{event.title || "Untitled event"}</strong>
                          <span>{formatCalendarEventTime(event.startAt)} - {formatCalendarEventTime(event.endAt)}</span>
                        </div>
                        <span className="dashboard-calendar-item-meta">
                          {(event.description || "").trim() || "No agenda or meeting note"}
                        </span>
                      </button>
                    ))}
                    {todayEventsSnapshot.events.length > 4 ? (
                      <button type="button" className="action-link dashboard-calendar-more" onClick={() => setActiveView("calendar")}>
                        View all {todayEventsSnapshot.events.length} events
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </section>
                <section className="notes-section dashboard-panel dashboard-recent-section">
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Recent activity</span>
                      <h2>Recent titles</h2>
                    </div>
                    <button className="action-link" onClick={() => setActiveView("titles")}>View all</button>
                  </div>

                  {titlesError ? <div className="feedback-panel error">{titlesError}</div> : null}
                  {loadingTitles ? <div className="feedback-panel">Loading titles...</div> : null}

                  {!loadingTitles && !recentTitles.length ? (
                    <div className="empty-state">
                      <h3>No titles yet</h3>
                      <p>Create your first title and start grouping related notes under it.</p>
                      <button
                        className="action-button"
                        onClick={() => {
                          setEditorState({ mode: "create-group" });
                          setActiveView("new-group");
                        }}
                      >
                        Create title
                      </button>
                    </div>
                  ) : null}

                  {!!recentTitles.length && (
                    <RecentTitlesSlider>
                      {recentTitles.map((group) => (
                        <NoteCard
                          key={group.id}
                          variant="dashboard"
                          title={group.title || "Untitled"}
                          icon={group.icon}
                          color={group.color}
                          meta={`${group.note_count || 0} note${group.note_count === 1 ? "" : "s"} inside`}
                          excerpt={group.preview || ""}
                          author={group.author || user?.name || user?.email || "You"}
                          date={formatDate(group.updated_at || group.created_at)}
                          temp={group.temp}
                          onOpen={() => openGroup(group)}
                          onDelete={() => handleDeleteGroup(group.id)}
                          isFolderDropTarget={folderCardDropTarget === group.id}
                          onFolderDragOver={(event) => handleTitleCardFolderDragOver(event, group.id)}
                          onFolderDragLeave={() => setFolderCardDropTarget((current) => current === group.id ? "" : current)}
                          onFolderDrop={(event) => handleTitleCardFolderDrop(event, group)}
                        />
                      ))}
                    </RecentTitlesSlider>
                  )}
                </section>

            <section className="dashboard-grid">
              <section className="dashboard-stack dashboard-stack-primary">

                <details className="dashboard-panel dashboard-about-panel"><summary>About AddaNote</summary>
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">About</span>
                      <h2>Built for organized technical notes</h2>
                    </div>
                  </div>
                  <p>
                    AddaNote keeps project notes, lab logs, and troubleshooting history inside grouped
                    titles so related work stays together and is easier to search later.
                  </p>
                  <ul className="dashboard-feature-list">
                    <li>Store multiple child notes inside one title container.</li>
                    <li>Keep folder paths and note history easier to browse.</li>
                    <li>Open the title library when you need search, filtering, or navigation.</li>
                  </ul>
                </details>

                <details className="dashboard-panel dashboard-contact-panel"><summary>Backup and restore</summary>
                  <span className="eyebrow">Backup</span>
                  <h2>Export all notes and import all notes</h2>
                  <p>Download every title and note in one JSON backup file, or restore a backup file into your workspace.</p>
                  {(notesTransferStatus.lastImportAt || notesTransferStatus.lastExportAt) ? (
                    <div className="import-export-status">
                      {notesTransferStatus.lastExportAt ? <span>Last export: {formatTransferTimestamp(notesTransferStatus.lastExportAt)}</span> : null}
                      {notesTransferStatus.lastImportAt ? <span>Last import: {formatTransferTimestamp(notesTransferStatus.lastImportAt)}</span> : null}
                    </div>
                  ) : null}
                  <div className="dashboard-actions">
                    <button
                      className="action-button"
                      onClick={handleExportAllNotes}
                      disabled={notesExporting || notesImporting}
                    >
                      {notesExporting ? "Exporting..." : "Export all notes"}
                    </button>
                    <button
                      className="action-button action-button-secondary"
                      onClick={() => notesImportInputRef.current?.click()}
                      disabled={notesImporting || notesExporting}
                    >
                      {notesImporting ? "Importing..." : "Import all notes"}
                    </button>
                    <input
                      ref={notesImportInputRef}
                      type="file"
                      accept="application/json"
                      onChange={handleImportAllNotes}
                      className="visually-hidden-input"
                    />
                  </div>
                  {notesTransferMessage ? (
                    <div className={`feedback-panel${notesTransferMessage.startsWith("Imported ") ? "" : " error"}`}>{notesTransferMessage}</div>
                  ) : null}
                </details>
              </section>

              <aside className="dashboard-stack dashboard-stack-secondary">
                <details className="dashboard-panel dashboard-quickstart"><summary>Workspace shortcuts</summary>
                  <span className="eyebrow">Quick Start</span>
                  <h2>Move with fewer clicks</h2>
                  <p>Use the dashboard for summary, open the full title library when you need search, and create new note containers from a dedicated screen.</p>
                  <div className="dashboard-actions">
                    <button className="action-button" onClick={() => setActiveView("titles")}>Browse titles</button>
                    <button
                      className="action-button action-button-secondary"
                      onClick={() => {
                        setEditorState({ mode: "create-group" });
                        setActiveView("new-group");
                      }}
                    >
                      New note
                    </button>
                  </div>
                </details>

                <details className="dashboard-panel dashboard-news-panel"><summary>News</summary>
                  <div className="section-heading">
                    <div>
                      <span className="eyebrow">Latest News</span>
                      <h2>Charter Communications</h2>
                    </div>
                    <a
                      className="action-link"
                      href="https://news.google.com/search?q=Charter%20Communications&hl=en-US&gl=US&ceid=US:en"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View all
                    </a>
                  </div>

                  {charterNewsSnapshot.loading ? <div className="feedback-panel">Loading news...</div> : null}
                  {!charterNewsSnapshot.loading && charterNewsSnapshot.error ? (
                    <div className="feedback-panel error">{charterNewsSnapshot.error}</div>
                  ) : null}
                  {!charterNewsSnapshot.loading && !charterNewsSnapshot.error && !charterNewsSnapshot.items.length ? (
                    <div className="dashboard-news-empty">
                      <strong>No recent headlines found</strong>
                      <span>Try again later for current Charter Communications coverage.</span>
                    </div>
                  ) : null}
                  {!charterNewsSnapshot.loading && !charterNewsSnapshot.error && !!charterNewsSnapshot.items.length ? (
                    <div className="dashboard-news-list">
                      {charterNewsSnapshot.items.map((item) => (
                        <a
                          key={`${item.link}-${item.publishedAt}`}
                          className="dashboard-news-item"
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <strong>{item.title}</strong>
                          <span>{item.source || "News source"}</span>
                          <span>{item.publishedAt ? formatDate(item.publishedAt) : ""}</span>
                        </a>
                      ))}
                    </div>
                  ) : null}
                </details>

                <details className="dashboard-panel dashboard-market-panel"><summary>Markets and weather</summary>
                  <span className="eyebrow">Market And Weather</span>
                  <div className="dashboard-market-grid">
                    <a
                      className="dashboard-market-card"
                      href="https://www.google.com/finance/quote/.DJI:INDEXDJX"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="dashboard-market-symbol">DJIA</span>
                      <strong>Dow Jones Industrial Average</strong>
                      <span>Open market page</span>
                    </a>
                    <a
                      className="dashboard-market-card"
                      href="https://www.google.com/finance/quote/.INX:INDEXSP"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="dashboard-market-symbol">S&amp;P 500</span>
                      <strong>S&amp;P 500 Index</strong>
                      <span>Open market page</span>
                    </a>
                  </div>
                  <div className="dashboard-weather-card">
                    <form className="dashboard-weather-search" onSubmit={handleWeatherSearchSubmit}>
                      <label className="dashboard-weather-search-field">
                        <span>Weather search</span>
                        <input
                          type="text"
                          className="form-control"
                          value={weatherQuery}
                          onChange={(event) => setWeatherQuery(event.target.value)}
                          placeholder="ZIP code or city, state"
                        />
                      </label>
                      <div className="dashboard-weather-search-actions">
                        <button type="submit" className="action-button action-button-secondary">Search</button>
                        <button type="button" className="action-link" onClick={handleUseCurrentWeatherLocation}>Use my location</button>
                      </div>
                    </form>
                    {weatherSearchMessage ? <p className="dashboard-weather-copy">{weatherSearchMessage}</p> : null}
                    <div className="dashboard-weather-heading">
                      <div>
                        <strong>Local weather</strong>
                        <span>{weatherSnapshot.label || "Browser location"}</span>
                      </div>
                      {weatherSnapshot.updatedAt ? (
                        <time dateTime={weatherSnapshot.updatedAt}>
                          {formatTransferTimestamp(weatherSnapshot.updatedAt)}
                        </time>
                      ) : null}
                    </div>

                    {weatherSnapshot.loading ? <p className="dashboard-weather-copy">Loading current weather...</p> : null}
                    {!weatherSnapshot.loading && weatherSnapshot.error ? (
                      <p className="dashboard-weather-copy">{weatherSnapshot.error}</p>
                    ) : null}

                    {!weatherSnapshot.loading && !weatherSnapshot.error ? (
                      <>
                        <div className="dashboard-weather-main">
                          <strong>{weatherSnapshot.temperatureF !== "" ? `${weatherSnapshot.temperatureF}°F` : "--"}</strong>
                          <span>{weatherSnapshot.condition || "Current conditions"}</span>
                        </div>
                        <div className="dashboard-weather-meta">
                          <span>Feels like {weatherSnapshot.apparentTemperatureF !== "" ? `${weatherSnapshot.apparentTemperatureF}°F` : "--"}</span>
                          <span>Wind {weatherSnapshot.windSpeedMph !== "" ? `${weatherSnapshot.windSpeedMph} mph` : "--"}</span>
                          <span>Humidity {weatherSnapshot.humidity !== "" ? `${weatherSnapshot.humidity}%` : "--"}</span>
                          <span>Precipitation {weatherSnapshot.precipitation !== "" ? `${weatherSnapshot.precipitation} mm` : "--"}</span>
                        </div>
                        {weatherSnapshot.timezone ? (
                          <p className="dashboard-weather-copy">Updated for {weatherSnapshot.timezone}.</p>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </details>
              </aside>
            </section>

          </>
        )}

        {activeView === "titles" && (
          <section className="notes-section">
            <div className="section-heading section-heading-compact">
              <div className="library-heading-block">
                {isMobileSidebar || sidebarCollapsed ? (
                  <button
                    type="button"
                    className="mobile-menu-button library-menu-button"
                    ref={mobileMenuButtonRef}
                    onClick={openSidebar}
                    aria-label="Open menu"
                  >
                    <span className="icon-button-content">
                      <HamburgerIcon />
                    </span>
                  </button>
                ) : null}
                <div>
                <span className="eyebrow">Organized titles</span>
                <h2>Title Library</h2>
                </div>
              </div>
              <div className="section-actions section-actions-compact">
                <button
                  className="action-button action-button-secondary"
                  onClick={handleExportAllNotes}
                  disabled={notesExporting || notesImporting}
                >
                  {notesExporting ? "Exporting..." : "Export all notes"}
                </button>
                <button
                  className="action-button action-button-secondary"
                  onClick={() => notesImportInputRef.current?.click()}
                  disabled={notesImporting || notesExporting}
                >
                  {notesImporting ? "Importing..." : "Import all notes"}
                </button>
                <button
                  className="action-button"
                  onClick={() => {
                    setEditorState({ mode: "create-group" });
                    setActiveView("new-group");
                  }}
                >
                  New note
                </button>
              </div>
            </div>
            <input
              ref={notesImportInputRef}
              type="file"
              accept="application/json"
              onChange={handleImportAllNotes}
              className="visually-hidden-input"
            />
            {(notesTransferStatus.lastImportAt || notesTransferStatus.lastExportAt) ? (
              <div className="import-export-status">
                {notesTransferStatus.lastExportAt ? <span>Last export: {formatTransferTimestamp(notesTransferStatus.lastExportAt)}</span> : null}
                {notesTransferStatus.lastImportAt ? <span>Last import: {formatTransferTimestamp(notesTransferStatus.lastImportAt)}</span> : null}
              </div>
            ) : null}

            <form className="search-form search-form-compact" onSubmit={handleSearchSubmit}>
              <div className="search-autocomplete">
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => {
                    if (searchInput.trim().length >= 2) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    window.setTimeout(() => setShowSuggestions(false), 150);
                  }}
                  placeholder="Search title names or note contents"
                  aria-label="Search titles"
                />
                {showSuggestions && (loadingSuggestions || searchSuggestions.length > 0) ? (
                  <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
                    {loadingSuggestions ? (
                      <div className="search-suggestion is-status">Searching...</div>
                    ) : (
                      searchSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="search-suggestion"
                          onMouseDown={() => handleSuggestionSelect(suggestion)}
                        >
                          <strong>{suggestion.title}</strong>
                          <span>{suggestion.note_count || 0} note{suggestion.note_count === 1 ? "" : "s"}</span>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
              {searchTerm ? <button type="button" className="action-link search-clear-link" onClick={clearSearch}>Clear</button> : null}
              <button type="submit" className="action-button action-button-secondary">Search</button>
            </form>

            {titlesError ? <div className="feedback-panel error">{titlesError}</div> : null}
            {notesTransferMessage ? (
              <div className={`feedback-panel${notesTransferMessage.startsWith("Imported ") ? "" : " error"}`}>{notesTransferMessage}</div>
            ) : null}
            {loadingTitles ? <div className="feedback-panel">Loading titles...</div> : null}

            {!loadingTitles && !titles.length ? (
              <div className="empty-state">
                <h3>No titles found</h3>
                <p>
                  {searchTerm
                    ? "Try a broader search term or clear the filter."
                    : "Create your first title and its first note."}
                </p>
                <button
                  className="action-button"
                  onClick={() => {
                    setEditorState({ mode: "create-group" });
                    setActiveView("new-group");
                  }}
                >
                  Create title
                </button>
              </div>
            ) : null}

            {!!titles.length && (
              <>
                <div className="notes-grid">
                  {titles.map((group) => (
                    <NoteCard
                      key={group.id}
                      title={group.title || "Untitled"}
                      icon={group.icon}
                      color={group.color}
                      meta={`${group.note_count || 0} note${group.note_count === 1 ? "" : "s"} inside`}
                      excerpt={group.preview || ""}
                      author={group.author || user?.name || user?.email || "You"}
                      date={formatDate(group.updated_at || group.created_at)}
                      temp={group.temp}
                      onOpen={() => openGroup(group)}
                      onDelete={() => handleDeleteGroup(group.id)}
                      isFolderDropTarget={folderCardDropTarget === group.id}
                      onFolderDragOver={(event) => handleTitleCardFolderDragOver(event, group.id)}
                      onFolderDragLeave={() => setFolderCardDropTarget((current) => current === group.id ? "" : current)}
                      onFolderDrop={(event) => handleTitleCardFolderDrop(event, group)}
                    />
                  ))}
                </div>

                <div className="pagination-bar">
                  <button
                    className="action-button action-button-secondary"
                    disabled={offset === 0 || loadingTitles}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </button>
                  <div className="pagination-summary">
                    Showing {total === 0 ? 0 : offset + 1} - {Math.min(offset + PAGE_SIZE, total)} of {total}
                  </div>
                  <button
                    className="action-button action-button-secondary"
                    disabled={offset + PAGE_SIZE >= total || loadingTitles}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {activeView === "generator" && user?.isAdmin && (
          <ConfigGeneratorView />
        )}

        {activeView === "flights" && <FlightsView />}

        {activeView === "calendar" && (
          <CalendarView />
        )}

        {activeView === "learning" && (
          <LearningView
            pageType={learningPageType}
            entityId={learningPageId}
            onNavigate={openLearningRoute}
            onOpenLesson={openLearningLesson}
          />
        )}

        {activeView === "learning-lesson" && learningLessonItemId && (
          <LearningLessonView
            itemId={learningLessonItemId}
            onBack={() => openLearningRoute(learningPageType, learningPageId)}
            onGoHome={goHome}
          />
        )}

        {activeView === "group" && selectedGroup && (
          <NoteGroupView
            groupId={selectedGroup.id}
            refreshToken={groupRefreshToken}
            initialFolderPath={groupInitialFolderPath}
            initialExpandedEntryId={groupInitialEntryId}
            onDataChanged={async (affectedGroupIds = [selectedGroup.id]) => {
              fetchTitles();
              invalidateSidebarChildren(affectedGroupIds);
              await syncSidebarChildrenForGroups(affectedGroupIds);
            }}
            onBack={() => {
              setActiveView("titles");
              setSelectedGroup(null);
              setGroupRefreshToken(0);
              setGroupInitialFolderPath("");
              setGroupInitialEntryId("");
              fetchTitles();
            }}
            onCreateEntry={(group, folderPath = "") => {
              setSelectedGroup(group);
              setGroupInitialFolderPath(folderPath);
              setGroupInitialEntryId("");
              setEditorState({ mode: "create-entry", group, folderPath });
              setActiveView("entry-editor");
            }}
            onEditEntry={(entry, group) => {
              setSelectedGroup(group);
              setGroupInitialFolderPath(entry.folder_path || "");
              setGroupInitialEntryId(entry.id || "");
              setEditorState({ mode: "edit-entry", group, entry, folderPath: entry.folder_path || "" });
              setActiveView("entry-editor");
            }}
          />
        )}

        {activeView === "profile" && (
          <ProfileSettingsForm
            user={user}
            onUserUpdated={(nextUser) => setUser(nextUser)}
            onBack={() => setActiveView("dashboard")}
          />
        )}

        {activeView === "new-group" && (
          <Suspense fallback={<div className="feedback-panel">Loading editor...</div>}>
            <NoteView
              mode="create-group"
              onCreate={handleOptimisticCreate}
              onReplace={handleReplaceTemp}
              onRevert={handleRevertTemp}
              onClose={() => {
                setEditorState(null);
                setActiveView("dashboard");
                fetchTitles({ nextOffset: 0 });
                setOffset(0);
              }}
            />
          </Suspense>
        )}

        {activeView === "entry-editor" && editorState?.group && (
          <Suspense fallback={<div className="feedback-panel">Loading editor...</div>}>
            <NoteView
              id={editorState.entry?.id}
              groupId={editorState.group.id}
              groupTitle={editorState.group.title}
              initialFolderPath={editorState.folderPath || ""}
              mode={editorState.mode}
              onClose={(returnContext = {}) => {
                const returnEntryId = returnContext.entryId || editorState.entry?.id || "";
                const returnFolderPath = returnContext.folderPath ?? editorState.folderPath ?? "";
                setGroupRefreshToken((current) => current + 1);
                setSelectedGroup(editorState.group);
                setGroupInitialFolderPath(returnFolderPath);
                setGroupInitialEntryId(returnEntryId);
                setEditorState(null);
                setActiveView("group");
                fetchTitles();
                invalidateSidebarChildren([editorState.group.id]);
                syncSidebarChildrenForGroups([editorState.group.id]);
              }}
            />
          </Suspense>
        )}

        {SelectedLegacyView && (
          <Suspense fallback={<div className="feedback-panel">Loading module...</div>}>
            <SelectedLegacyView key={activeView} />
          </Suspense>
        )}

        <footer className="workspace-footer">
          <div className="workspace-footer-copy">
            <strong>AddaNote</strong>
            <p>Secure grouped notes for projects, labs, and troubleshooting history.</p>
          </div>
          <div className="workspace-footer-links" aria-label="Footer links">
            <button type="button" className="action-link" onClick={goHome}>Workspace</button>
            <button type="button" className="action-link" onClick={() => setActiveView("titles")}>Titles</button>
            <button type="button" className="action-link" onClick={() => setActiveView("learning")}>Learning</button>
            <button type="button" className="action-link" onClick={() => setActiveView("calendar")}>Calendar</button>
            {user?.isAdmin ? (
              <button type="button" className="action-link" onClick={() => setActiveView("generator")}>Generator</button>
            ) : null}
            <button type="button" className="action-link" onClick={() => setActiveView("profile")}>Profile</button>
          </div>
          <ThemeControl />
          <div className="workspace-footer-meta">
            <span>Need help? Start in Profile or open the title library.</span>
            <span>© {new Date().getFullYear()} AddaNote</span>
          </div>
        </footer>
      </main>
    </div>
  );
}










